import { InfoTooltip } from 'components/info-tooltip';
import type { ReactNode } from 'react';
import * as React from 'react';
import { cn } from 'utils/tailwind';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  after?: ReactNode | string;
  info?: string;
  invalid?: boolean;
  inputClassName?: string;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, inputClassName, type, after, info, invalid, ...props },
    ref,
  ) => {
    return (
      <div
        className={cn(
          'outline-none overflow-hidden flex w-full justify-between rounded-[.625rem] border border-solid border-neutral-200 text-base text-primary-black ring-offset-background file:border-0 file:bg-transparent file:font-normal file:text-sm placeholder:text-neutral-400 disabled:cursor-not-allowed [&:has(input:focus-visible)]:border-primary-black [&:has(input:focus-visible)]:border-solid [&:has(input[aria-invalid=true])]:border-system-red [&:has(input[aria-invalid=true]:focus-visible)]:border-system-red',
          invalid && '!border-system-red',
          props.disabled && 'cursor-not-allowed bg-neutral-100',
          className,
        )}
      >
        <input
          className={cn(
            'h-12 w-full bg-transparent py-3 focus-visible:outline-none px-2.5',
            props.value && 'pt-4 pb-1',
            props.disabled && 'cursor-not-allowed',
            'placeholder:opacity-0 focus:placeholder:opacity-100',
            inputClassName,
          )}
          ref={ref}
          type={type}
          {...props}
        />
        {after ? after : null}
        {info ? (
          <div className="flex items-center pr-2.5">
            <InfoTooltip
              className={cn(props.disabled && 'cursor-not-allowed')}
              text={info}
              iconClassName="h-4 w-4 text-neutral-400"
            />
          </div>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
